import { useCallback, useEffect, useRef, useState } from "react";
import type { AspectMode, MediaTrack } from "@hybrid-iptv/player";
import { usePlayerStore } from "./playerStore.js";
import { usePlayerSettings } from "./playerSettingsStore.js";
import { SLEEP_CHOICES, sleepLengthShort, useSleep } from "./sleepTimer.js";
import { recordViewerChoice } from "./trackMemory.js";

const ASPECTS: Array<{ id: AspectMode; label: string; hint: string }> = [
  { id: "fit", label: "Fit", hint: "The picture's own shape — bars where it does not fill the screen" },
  { id: "fill", label: "Fill", hint: "Zoom until the bars are gone — the edges are cropped" },
  { id: "stretch", label: "Stretch", hint: "Stretch to the screen — the picture is distorted" },
  { id: "zoom", label: "Zoom", hint: "Zoom in a third — crops black bars that are part of the picture (full screen)" },
];

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * The player's own settings, over the picture from the gear in the top-left
 * corner: how the picture fills the screen, subtitles, the audio track, and
 * the speed of anything that is not live.
 *
 * Tracks are read from the ENGINE each time this opens (hls.js's playlist
 * tracks on the website, ExoPlayer's on the device) rather than kept in
 * state — they belong to the stream now playing, and a channel change
 * replaces them wholesale.
 */
export function PlayerSettingsMenu({ onClose }: { onClose: () => void }) {
  const player = usePlayerStore((state) => state.player);
  const source = usePlayerStore((state) => state.source);
  const isLive = source?.type === "live";
  const endsAt = useSleep((state) => state.endsAt);
  const setSleep = useSleep((state) => state.setSleep);
  const aspect = usePlayerSettings((state) => state.aspect);
  const setAspect = usePlayerSettings((state) => state.setAspect);
  const rate = usePlayerSettings((state) => state.rate);
  const setRate = usePlayerSettings((state) => state.setRate);
  const [tracks, setTracks] = useState<MediaTrack[] | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const readTracks = useCallback(() => {
    if (!player?.tracks) {
      setTracks([]);
      return;
    }
    void player
      .tracks()
      .then(setTracks)
      .catch(() => setTracks([]));
  }, [player]);

  useEffect(readTracks, [readTracks]);

  // Open with focus inside, so a remote lands on the first choice rather than
  // wherever it was on the page behind.
  useEffect(() => {
    panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, []);

  // Escape (and a TV remote's Back, which arrives as a history pop — see
  // PlayerSurface) closes the menu, never the player.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const chooseAspect = (mode: AspectMode) => {
    setAspect(mode);
    player?.setAspectMode?.(mode);
  };

  const chooseRate = (next: number) => {
    setRate(next);
    player?.setPlaybackRate?.(next);
  };

  const chooseTrack = (kind: "audio" | "text", id: string | null) => {
    // The viewer's own choice: kept for this live channel and profile (player/trackMemory.ts).
    if (tracks) recordViewerChoice(tracks, kind, id);
    player?.selectTrack?.(kind, id);
    // Ask the engine what it ended up with rather than assuming: a stream can
    // refuse a track (no decoder for it), and the tick shows the truth.
    setTimeout(readTracks, 150);
  };

  const audio = (tracks ?? []).filter((track) => track.kind === "audio");
  const text = (tracks ?? []).filter((track) => track.kind === "text");
  const subtitlesOn = text.some((track) => track.selected);

  return (
    <div className="player-menu" role="dialog" aria-label="Player settings" ref={panelRef}>
      <div className="player-menu__head">
        <span className="player-menu__heading">Player</span>
        <button type="button" className="player-menu__close" onClick={onClose} aria-label="Close player settings">
          ✕
        </button>
      </div>

      <div className="player-menu__group">
        <h3 className="player-menu__label">Aspect ratio</h3>
        <div className="player-menu__options">
          {ASPECTS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`player-menu__option ${aspect === option.id ? "player-menu__option--on" : ""}`}
              onClick={() => chooseAspect(option.id)}
              title={option.hint}
              aria-pressed={aspect === option.id}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="player-menu__group">
        <h3 className="player-menu__label">Subtitles</h3>
        <div className="player-menu__options">
          <button
            type="button"
            className={`player-menu__option ${subtitlesOn ? "" : "player-menu__option--on"}`}
            onClick={() => chooseTrack("text", null)}
            aria-pressed={!subtitlesOn}
          >
            Off
          </button>
          {text.map((track) => (
            <button
              key={track.id}
              type="button"
              className={`player-menu__option ${track.selected ? "player-menu__option--on" : ""}`}
              onClick={() => chooseTrack("text", track.id)}
              aria-pressed={track.selected}
            >
              {track.label}
            </button>
          ))}
        </div>
        {tracks !== null && text.length === 0 && (
          <p className="player-menu__note">This stream carries no subtitles.</p>
        )}
      </div>

      {audio.length > 1 && (
        <div className="player-menu__group">
          <h3 className="player-menu__label">Audio</h3>
          <div className="player-menu__options">
            {audio.map((track) => (
              <button
                key={track.id}
                type="button"
                className={`player-menu__option ${track.selected ? "player-menu__option--on" : ""}`}
                onClick={() => chooseTrack("audio", track.id)}
                aria-pressed={track.selected}
              >
                {track.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isLive && (
        <div className="player-menu__group">
          <h3 className="player-menu__label">Speed</h3>
          <div className="player-menu__options">
            {RATES.map((option) => (
              <button
                key={option}
                type="button"
                className={`player-menu__option ${rate === option ? "player-menu__option--on" : ""}`}
                onClick={() => chooseRate(option)}
                aria-pressed={rate === option}
              >
                {option === 1 ? "Normal" : `${option}×`}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="player-menu__group">
        <h3 className="player-menu__label">
          Sleep timer
          {endsAt ? ` — stops in ${sleepLengthShort(Math.max(1, Math.round((endsAt - Date.now()) / 60_000)))}` : ""}
        </h3>
        <div className="player-menu__options">
          <button
            type="button"
            className={`player-menu__option ${endsAt === null ? "player-menu__option--on" : ""}`}
            onClick={() => setSleep(null)}
            aria-pressed={endsAt === null}
          >
            Off
          </button>
          {SLEEP_CHOICES.map((minutes) => (
            <button key={minutes} type="button" className="player-menu__option" onClick={() => setSleep(minutes)}>
              {sleepLengthShort(minutes)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
